import React from 'react';
import { Info, Car, Gamepad2, Trophy, Zap } from 'lucide-react';

export const About = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Info className="mr-2" size={32} />
        <h1 className="text-4xl font-bold">About React Racing Game</h1>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-8">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <Car className="mr-2 text-blue-500" size={24} />
            <h2 className="text-2xl font-semibold">The Game</h2>
          </div>

          <p className="text-lg mb-4">
            Welcome to React Racing Game - an exciting, fast-paced racing experience built entirely with React!
            This simple yet addictive game pits you against an AI-powered computer opponent in a thrilling race to the finish line.
          </p>

          <p className="text-lg mb-6">
            The concept is beautifully simple: press the SPACE key repeatedly to move your blue car forward while the computer's red car moves automatically.
            The first to cross the yellow finish line wins! It's a test of speed, reflexes, and endurance.
          </p>

          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <h3 className="font-semibold mb-2 flex items-center">
              <Gamepad2 className="mr-2" size={18} />
              How to Play:
            </h3>
            <ul className="space-y-1 text-sm">
              <li>• Click "Start Race" or press SPACE to begin</li>
              <li>• Keep pressing SPACE to move your car forward</li>
              <li>• Race against the computer to reach the finish line first</li>
              <li>• Your score is tracked across multiple races</li>
              <li>• Click "Reset All" to start fresh</li>
            </ul>
          </div>

          <hr className="my-6 border-gray-200" />

          <div className="flex items-center mb-4">
            <Zap className="mr-2 text-yellow-500" size={24} />
            <h2 className="text-xl font-semibold">Game Features</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold mb-2">🏎️ Real-time Racing</h4>
              <p className="text-sm text-gray-600">
                Experience smooth, real-time racing with fluid animations and responsive controls.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold mb-2">🤖 Smart AI Opponent</h4>
              <p className="text-sm text-gray-600">
                The computer opponent uses dynamic speed variations to create challenging gameplay.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold mb-2">🏆 Score Tracking</h4>
              <p className="text-sm text-gray-600">
                Keep track of your wins and losses against the computer across multiple races.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold mb-2">⌨️ Keyboard Controls</h4>
              <p className="text-sm text-gray-600">
                Simple SPACE key controls make the game accessible and easy to play.
              </p>
            </div>
          </div>

          <hr className="my-6 border-gray-200" />

          <h2 className="text-xl font-semibold mb-4">Technical Implementation</h2>

          <p className="text-lg mb-4">
            This racing game showcases modern React development practices and demonstrates how to build
            interactive, real-time applications using React Hooks and state management.
          </p>

          <ul className="space-y-2 pl-5 mb-6">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <strong>React Hooks:</strong> useState and useEffect for game state management
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <strong>Real-time Updates:</strong> setInterval for smooth computer movement
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <strong>Event Handling:</strong> Keyboard event listeners for player input
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <strong>CSS Animations:</strong> Smooth transitions and dynamic positioning
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <strong>Responsive Design:</strong> Tailwind CSS for mobile-friendly gameplay
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <strong>Component Architecture:</strong> Modular, reusable React components
            </li>
          </ul>

          <div className="bg-yellow-50 p-4 rounded-md">
            <div className="flex items-center mb-2">
              <Trophy className="mr-2 text-yellow-600" size={18} />
              <h3 className="font-semibold">Pro Tips</h3>
            </div>
            <ul className="text-sm space-y-1">
              <li>• Find a consistent rhythm when pressing SPACE for optimal speed</li>
              <li>• The computer's speed varies slightly, so don't give up if you fall behind</li>
              <li>• Practice makes perfect - your reaction time will improve with each race</li>
              <li>• Challenge friends to see who can get the highest score!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
